// The inspection record's list tabs from legacy (H98, Aaron 2026-09-28: "need
// the rest of the tabs from legacy"). Legacy's inspection record is an
// aircraft_templates record (current_object = inspection); these are its
// Config List, BOM, Linked To, ATA History, Task/Check List and Special Tasks
// tabs (Task/Check List is shared with the AD/SB record), each reading the
// backend route that reproduces that tab's own SQL (app/inspections/
// record_tabs.py — the file:line of each legacy query is on its function there). Columns are legacy's headings, in legacy's order.
//
// Which of these show on a record is decided by GET /v6/inspection/record-tabs
// (see useInspectionRecordTabs.tsx), never here.
import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";

import AlertDialog from "../../components/AlertDialog";
import type { ActionMenuEntry } from "../../components/datagrid/ActionMenu";
import { useSnackbar } from "notistack";
import RecordTabGrid from "./RecordTabGrid";
import { DEFAULT_TENANT_DATE_FORMAT, formatTenantDate, tenantDateValue, useTenantDateFormat } from "../../utils/tenantDate";
import InspectionBomDialog from "../dialogs/InspectionBomDialog";
import {
  getInspectionAtaHistory,
  getInspectionBom,
  getInspectionConfigList,
  getInspectionLinkedTo,
  getInspectionSpecialTasks,
  getInspectionTaskList,
  removeInspectionBomLine,
} from "../inspections_tr";
import type {
  InspectionAtaHistoryRow,
  InspectionBomRow,
  InspectionConfigListRow,
  InspectionLinkedComponent,
  InspectionLinkedPart,
  InspectionSpecialTask,
  InspectionTaskListRow,
} from "../types";

interface TabProps {
  inspectionId: number;
}

// Decimals arrive as JSON strings (Pydantic condecimal); Number() them so the
// grid sorts numerically and 250.0000 reads 250.
const num = (v: unknown) => (v === null || v === undefined || v === "" ? null : Number(v));

const dash = (
  <Typography variant="body2" color="text.disabled" component="span">
    &mdash;
  </Typography>
);

// ── Config List — tab_config_list.inc: @ | Model | ATA# | Based On | Period | Warning | Warning Unit
const CONFIG_COLUMNS: GridColDef<InspectionConfigListRow>[] = [
  { field: "model", headerName: "Model", flex: 1 },
  { field: "ata_nos", headerName: "ATA#", flex: 0.6 },
  { field: "based_on", headerName: "Based On", flex: 1 },
  { field: "period", headerName: "Period", flex: 0.6, type: "number", valueGetter: (v) => num(v) },
  { field: "warning", headerName: "Warning", flex: 0.6, type: "number", valueGetter: (v) => num(v) },
  { field: "warning_unit", headerName: "Warning Unit", flex: 0.8 },
];

export function InspectionConfigListTab({ inspectionId }: TabProps) {
  const load = useCallback(() => getInspectionConfigList(inspectionId), [inspectionId]);
  return (
    <RecordTabGrid
      name="inspection-record-config-list"
      what="Config List"
      testId="inspection-config-list-tab"
      load={load}
      columns={CONFIG_COLUMNS}
      noRowsLabel="No Limitation Added"
    />
  );
}

// ── BOM — ajax/aircraft_templates_bom.inc: Part Number | Description | Quantity
const BOM_COLUMNS: GridColDef<InspectionBomRow>[] = [
  { field: "number", headerName: "Part Number", flex: 0.8 },
  { field: "name", headerName: "Description", flex: 1.4 },
  { field: "quantity", headerName: "Quantity", flex: 0.5, type: "number", valueGetter: (v) => num(v) },
];

// Writable (Aaron 2026-10-06, Peak 2554: "Bom tab needs a plus to add parts"):
// (+) adds a part, ACTIONS → Edit Quantity / Remove — legacy's three writes
// (ajax/aircraft_templates_bom.inc:1-36). Remove is soft (active = 0), so it
// says Remove, not Delete (PATTERNS.md BP-27).
export function InspectionBomTab({ inspectionId }: TabProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [refreshToken, setRefreshToken] = useState(0);
  const refresh = useCallback(() => setRefreshToken((t) => t + 1), []);
  const [dialog, setDialog] = useState<{ editing: InspectionBomRow | null } | null>(null);
  const [removing, setRemoving] = useState<InspectionBomRow[]>([]);
  const load = useCallback(() => getInspectionBom(inspectionId), [inspectionId]);

  const actions = useMemo<ActionMenuEntry[]>(() => {
    const picked = (payload: any): InspectionBomRow[] =>
      payload?.selectedRows ? Array.from(payload.selectedRows) : [];
    return [
      {
        name: "Edit Quantity",
        action: ["Edit_Bom_Quantity", "EDIT_BOM_QUANTITY"],
        requiresSelection: true,
        preventMultiSelect: true,
        multiSelectErrorMessage: "Select a single line to edit.",
        isReturnDataSelection: true,
        onClick: (payload: any) => {
          const [row] = picked(payload);
          if (row) setDialog({ editing: row });
        },
      },
      {
        name: "Remove",
        action: ["Remove_Bom_Line", "REMOVE_BOM_LINE"],
        requiresSelection: true,
        multipleSelection: true,
        isReturnDataSelection: true,
        onClick: (payload: any) => {
          const rows = picked(payload);
          if (rows.length) setRemoving(rows);
        },
      },
    ];
  }, []);

  const confirmRemove = async () => {
    const rows = removing;
    setRemoving([]);
    try {
      for (const r of rows) await removeInspectionBomLine(inspectionId, r.id);
      enqueueSnackbar(`${rows.length} removed`, { variant: "success" });
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.detail ?? "Remove failed", { variant: "error" });
    } finally {
      refresh();
    }
  };

  return (
    <>
      <RecordTabGrid
        name="inspection-record-bom"
        what="BOM"
        testId="inspection-bom-tab"
        load={load}
        columns={BOM_COLUMNS}
        noRowsLabel="No BOM found for the current template."
        onCreate={() => setDialog({ editing: null })}
        actions={actions}
        refreshToken={refreshToken}
      />
      <InspectionBomDialog
        open={!!dialog}
        inspectionId={inspectionId}
        editing={dialog?.editing ?? null}
        onClose={() => setDialog(null)}
        onSaved={() => {
          setDialog(null);
          refresh();
        }}
      />
      <AlertDialog
        open={removing.length > 0}
        title={`Remove ${removing.length} BOM line${removing.length === 1 ? "" : "s"}?`}
        description="The part stays in the catalogue; only this inspection's BOM line is removed."
        handleClose={() => setRemoving([])}
      >
        <Button onClick={() => setRemoving([])}>Cancel</Button>
        <Button color="error" onClick={confirmRemove}>
          Remove
        </Button>
      </AlertDialog>
    </>
  );
}

// ── Linked To — ajax/aircraft_templates_parts_list_tab.inc. Two named
// sections with DIFFERENT columns = two sibling grids (PATTERNS.md, "SIBLING
// named sections"), headed with legacy's own section titles.
const PART_COLUMNS: GridColDef<InspectionLinkedPart>[] = [
  { field: "number", headerName: "Part Number", flex: 0.8 },
  { field: "name", headerName: "Description", flex: 1.4 },
];

const COMPONENT_COLUMNS: GridColDef<InspectionLinkedComponent>[] = [
  { field: "number", headerName: "Part Number", flex: 0.8 },
  { field: "name", headerName: "Description", flex: 1.2 },
  { field: "registration", headerName: "Aircraft", flex: 0.6 },
  { field: "based_on", headerName: "Based On", flex: 0.8 },
  { field: "item_id", headerName: "Item Number", flex: 0.6 },
  { field: "sn", headerName: "Serial Number", flex: 0.8 },
];

export function InspectionLinkedToTab({ inspectionId }: TabProps) {
  // ONE read for both sections: the route returns {parts, components}.
  const read = useMemo(() => getInspectionLinkedTo(inspectionId), [inspectionId]);
  const loadParts = useCallback(async () => (await read).parts ?? [], [read]);
  const loadComponents = useCallback(async () => (await read).components ?? [], [read]);
  return (
    <Box data-testid="inspection-linked-to-tab">
      <Typography variant="subtitle2" sx={{ px: "50px", pt: 2 }}>
        Link Inspection to Master Part
      </Typography>
      <RecordTabGrid
        name="inspection-record-linked-parts"
        what="linked parts"
        load={loadParts}
        columns={PART_COLUMNS}
        noRowsLabel="No parts linked to this record"
      />
      <Typography variant="subtitle2" sx={{ px: "50px", pt: 2 }}>
        Link Inspection to Component
      </Typography>
      <RecordTabGrid
        name="inspection-record-linked-components"
        what="linked components"
        load={loadComponents}
        columns={COMPONENT_COLUMNS}
        noRowsLabel="No parts linked to this record"
      />
    </Box>
  );
}

// ── ATA History — tab_ata_list.inc: A/C | Job# | Task# | ATA# | ATA Title |
// ATA Task Number | ATA Task Title | ATA Desc | Status | Date | Updated By.
// Rows from the ata_link block carry ATA columns only, as in legacy. Date: the completion's
// LOCAL day in the tenant's format (SOP re-walk picker audit, 2026-10-14: new Date("…") showed
// the day before west of UTC, in the browser's locale).
const ataColumnsFor = (pattern: string): GridColDef<InspectionAtaHistoryRow>[] => [
  { field: "registration", headerName: "A/C", flex: 0.5 },
  { field: "job_id", headerName: "Job#", flex: 0.4 },
  { field: "task_id", headerName: "Task#", flex: 0.4 },
  { field: "ata_no", headerName: "ATA#", flex: 0.4 },
  { field: "ata_title", headerName: "ATA Title", flex: 0.9 },
  { field: "ata_task_number", headerName: "ATA Task Number", flex: 0.6 },
  { field: "ata_task_title", headerName: "ATA Task Title", flex: 0.9 },
  { field: "ata_description", headerName: "ATA Desc", flex: 1.4 },
  { field: "status", headerName: "Status", flex: 0.5 },
  {
    field: "date_completed",
    headerName: "Date",
    flex: 0.6,
    type: "date",
    valueGetter: (v) => tenantDateValue(v as string | null),
    valueFormatter: (value) => formatTenantDate(value as Date | null, pattern),
  },
  { field: "updated_by", headerName: "Updated By", flex: 0.6 },
];

export function InspectionAtaHistoryTab({ inspectionId }: TabProps) {
  const load = useCallback(() => getInspectionAtaHistory(inspectionId), [inspectionId]);
  const datePattern = useTenantDateFormat() ?? DEFAULT_TENANT_DATE_FORMAT;
  const columns = useMemo(() => ataColumnsFor(datePattern), [datePattern]);
  return (
    <RecordTabGrid
      name="inspection-record-ata-history"
      what="ATA history"
      testId="inspection-ata-history-tab"
      load={load}
      columns={columns}
      noRowsLabel="No ATA history found"
    />
  );
}

// ── Task/Check List — ajax/task_list.inc: Template ID | Title | Reference | Type | Priority.
// Legacy has ONE Task/Check List tab for every aircraft_templates record, keyed
// by the record's link_type_name (includes/aircraft_templates_variables.inc:64-81):
// the inspection record here, and the AD/SB record (ads-and-sbs/ADsAndSBsDetail,
// GET /v6/aircraft_ads/{id}/task-list). Both render TaskCheckListTab; only the
// read differs.
//
// Title and Reference live in the TEMPLATE database (global_template_db). When
// this tenant's database user cannot read it the backend returns them as null
// and they render as an em dash with that reason, never as a title borrowed
// from this tenant's own tasks table (a different task).
const TEMPLATE_DB_NOTE =
  "Held in the template database, which this install cannot read yet";

const TASK_COLUMNS: GridColDef<InspectionTaskListRow>[] = [
  { field: "template_id", headerName: "Template ID", flex: 0.5 },
  {
    field: "title",
    headerName: "Title",
    flex: 1.4,
    renderCell: (p) => p.value ?? <span title={TEMPLATE_DB_NOTE} aria-label={TEMPLATE_DB_NOTE}>{dash}</span>,
  },
  {
    field: "reference",
    headerName: "Reference",
    flex: 0.8,
    renderCell: (p) => p.value ?? <span title={TEMPLATE_DB_NOTE} aria-label={TEMPLATE_DB_NOTE}>{dash}</span>,
  },
  { field: "type", headerName: "Type", flex: 0.4 },
  { field: "priority", headerName: "Priority", flex: 0.4, type: "number", valueGetter: (v) => num(v) },
];

interface TaskCheckListTabProps {
  /** Grid id — unique per record kind (BP-80 rule "name"). */
  name: string;
  testId: string;
  /** The record's linked templates, in legacy's order (priority, template id). */
  load: () => Promise<InspectionTaskListRow[]>;
  /** Writer props (RecordTabGrid's): a record that links template tasks passes them (A431). */
  onCreate?: () => void;
  actions?: ActionMenuEntry[];
  refreshToken?: number;
}

/** Legacy's Task/Check List tab, for any record that carries one. */
export function TaskCheckListTab({ name, testId, load, onCreate, actions, refreshToken }: TaskCheckListTabProps) {
  return (
    <RecordTabGrid
      name={name}
      what="Task/Check List"
      testId={testId}
      load={load}
      columns={TASK_COLUMNS}
      noRowsLabel="No Task linked to the template"
      onCreate={onCreate}
      actions={actions}
      refreshToken={refreshToken}
    />
  );
}

export function InspectionTaskListTab({ inspectionId }: TabProps) {
  const load = useCallback(
    async () => (await getInspectionTaskList(inspectionId)).rows ?? [],
    [inspectionId]
  );
  return (
    <TaskCheckListTab name="inspection-record-task-list" testId="inspection-task-list-tab" load={load} />
  );
}

// ── Special Tasks — tab_special_tasks.inc: ID | Description | Based On | Period | Warning | Warning Unit
const SPECIAL_COLUMNS: GridColDef<InspectionSpecialTask>[] = [
  { field: "id", headerName: "ID", flex: 0.3 },
  { field: "description", headerName: "Description", flex: 1.6 },
  { field: "based_on", headerName: "Based On", flex: 0.8 },
  { field: "period", headerName: "Period", flex: 0.5, type: "number" },
  { field: "warning", headerName: "Warning", flex: 0.5, type: "number" },
  { field: "warning_unit", headerName: "Warning Unit", flex: 0.6 },
];

export function InspectionSpecialTasksTab({ inspectionId }: TabProps) {
  const load = useCallback(() => getInspectionSpecialTasks(inspectionId), [inspectionId]);
  return (
    <RecordTabGrid
      name="inspection-record-special-tasks"
      what="special tasks"
      testId="inspection-special-tasks-tab"
      load={load}
      columns={SPECIAL_COLUMNS}
      noRowsLabel="No special tasks recorded"
    />
  );
}
